import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  FC,
  useEffect,
  useMemo,
  Dispatch,
  useRef,
} from 'react';
import {
  UsersState,
  UsersAction,
  UsersActionType,
  UserType,
  UserCategory,
  UsersFilters,
} from '../users.interfaces';
import {
  BaseUser,
  ParentUser,
  VisitorUser,
  GuestUser,
  UsersSummary,
  VisitorsSummary,
} from '../../../../apis/users/users.api.interfaces';
import { useParentsSummaryHook } from '../hooks/useParentsSummaryHook';
import { useVisitorsSummaryHook } from '../hooks/useVisitorsSummaryHook';
import { useGuestsSummaryHook } from '../hooks/useGuestsSummaryHook';
import { useParentListHook } from '../hooks/useParentListHook';
import { useVisitorsListHook } from '../hooks/useVisitorsListHook';
import { useGuestListHook } from '../hooks/useGuestListHook';
import { useInternalUsersListHook } from '../hooks/useInternalUsersListHook';


// Initial state
const initialState: UsersState = {
  currentUserType: UserType.PARENTS,
  currentCategory: UserCategory.VISITOR,
  activeTabIndex: 0,
  parentUsers: [],
  visitorUsers: [],
  guestUsers: [],
  internalUsers: [],
  parentPagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0,
  },
  visitorPagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0,
  },
  guestPagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0,
  },
  internalPagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0,
  },
  parentsSummary: {
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    blockedUsers: 0,
    recentlyAdded: 0,
    statusDistribution: {
      active: 0,
      inactive: 0,
      pending: 0,
      blocked: 0,
    },
  },
  visitorsSummary: {
    totalVisitors: 0,
    checkedInVisitors: 0,
    checkedOutVisitors: 0,
    scheduledVisitors: 0,
  },
  guestsSummary: {
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    blockedUsers: 0,
    recentlyAdded: 0,
    statusDistribution: {
      active: 0,
      inactive: 0,
      pending: 0,
      blocked: 0,
    },
  },
  internalUsersSummary: {
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    blockedUsers: 0,
    recentlyAdded: 0,
    statusDistribution: {
      active: 0,
      inactive: 0,
      pending: 0,
      blocked: 0,
    },
  },
  filters: {
    userType: UserType.PARENTS,
    status: 'all',
    searchQuery: '',
    dateRange: {
      startDate: '',
      endDate: '',
    },
  },
  isLoading: false,
  error: null,
  selectedUser: null,
  isAddEditParentOpen: false,
  isAddEditVisitorOpen: false,
  isAddEditGuestOpen: false,
  isAddEditInternalOpen: false,
  isCreateMode: false,
  isEditMode: false,
};

// Reducer function
const usersReducer = (state: UsersState, action: UsersAction): UsersState => {
  switch (action.type) {
    case UsersActionType.SET_USER_TYPE:
      return {
        ...state,
        currentUserType: action.payload,
        filters: {
          ...state.filters,
          userType: action.payload,
        },
      };

    case UsersActionType.SET_ACTIVE_TAB:
      const tabToUserType = [UserType.PARENTS, UserType.VISITORS, UserType.GUESTS, UserType.INTERNAL_USERS];
      const tabToCategory = [UserCategory.VISITOR, UserCategory.GUARDIAN, UserCategory.STAFF, UserCategory.EXTERNAL, UserCategory.STUDENT];
      return {
        ...state,
        activeTabIndex: action.payload,
        currentUserType: tabToUserType[action.payload] || UserType.PARENTS,
        currentCategory: tabToCategory[action.payload] || UserCategory.VISITOR,
        filters: {
          ...state.filters,
          userType: tabToUserType[action.payload] || UserType.PARENTS,
        },
      };

    case UsersActionType.SET_CATEGORY:
      return { ...state, currentCategory: action.payload };

    case UsersActionType.SET_PARENT_USERS:
      return { ...state, parentUsers: action.payload };

    case UsersActionType.SET_VISITOR_USERS:
      return { ...state, visitorUsers: action.payload };

    case UsersActionType.SET_GUEST_USERS:
      return { ...state, guestUsers: action.payload };

    case UsersActionType.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case UsersActionType.SET_SEARCH_QUERY:
      return {
        ...state,
        filters: { ...state.filters, searchQuery: action.payload },
      };

    case UsersActionType.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case UsersActionType.SET_ERROR:
      return { ...state, error: action.payload };

    case UsersActionType.SET_SELECTED_USER:
      return { ...state, selectedUser: action.payload };

    case UsersActionType.UPDATE_PARENTS_SUMMARY:
      return { ...state, parentsSummary: action.payload };

    case UsersActionType.UPDATE_VISITORS_SUMMARY:
      return { ...state, visitorsSummary: action.payload };

    case UsersActionType.UPDATE_GUESTS_SUMMARY:
      return { ...state, guestsSummary: action.payload };

    case UsersActionType.RESET_FILTERS:
      return {
        ...state,
        filters: {
          ...initialState.filters,
          userType: state.currentUserType,
        },
      };

    case UsersActionType.SET_IS_ADD_EDIT_PARENT_OPEN:
      return { ...state, isAddEditParentOpen: action.payload };

    case UsersActionType.SET_IS_ADD_EDIT_VISITOR_OPEN:
      return { ...state, isAddEditVisitorOpen: action.payload };

    case UsersActionType.SET_IS_ADD_EDIT_GUEST_OPEN:
      return { ...state, isAddEditGuestOpen: action.payload };

    case UsersActionType.SET_IS_ADD_EDIT_INTERNAL_OPEN:
      return { ...state, isAddEditInternalOpen: action.payload };

    case UsersActionType.SET_INTERNAL_USERS:
      return { ...state, internalUsers: action.payload };

    case UsersActionType.SET_INTERNAL_PAGINATION:
      return { ...state, internalPagination: { ...state.internalPagination, ...action.payload } };

    case UsersActionType.UPDATE_INTERNAL_USERS_SUMMARY:
      return { ...state, internalUsersSummary: action.payload };

    case UsersActionType.SET_IS_CREATE_MODE:
      return {
        ...state,
        isCreateMode: action.payload,
        isEditMode: false,
      };

    case UsersActionType.SET_IS_EDIT_MODE:
      return {
        ...state,
        isEditMode: action.payload,
        isCreateMode: false,
      };

    default:
      return state;
  }
};

// Create contexts
const UsersContext = createContext<UsersState | undefined>(undefined);
const UsersDispatchContext = createContext<Dispatch<UsersAction> | undefined>(
  undefined
);
const UsersRefetchContext = createContext<
  | {
    parentList: () => Promise<any>;
    parentsSummary: () => Promise<any>;
    visitorList: () => Promise<any>;
    visitorsSummary: () => Promise<any>;
    guestList: () => Promise<any>;
    guestsSummary: () => Promise<any>;
  }
  | undefined
>(undefined);

// Provider component
interface UsersProviderProps {
  children: ReactNode;
}

export const UsersProvider: FC<UsersProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(usersReducer, initialState);

  // Default pagination params for list hooks
  const defaultPaginationParams = { page: 1, limit: 1000 };

  // Summary hooks
  const parentsSummaryQuery = useParentsSummaryHook();
  const visitorsSummaryQuery = useVisitorsSummaryHook({ enabled: state.activeTabIndex === 1 });
  const guestsSummaryQuery = useGuestsSummaryHook();

  // List hooks
  const parentListQuery = useParentListHook(defaultPaginationParams);
  const visitorListQuery = useVisitorsListHook(defaultPaginationParams, { enabled: state.activeTabIndex === 1 });
  const guestListQuery = useGuestListHook(defaultPaginationParams);
  const internalListQuery = useInternalUsersListHook(defaultPaginationParams);

  // Store refetch functions in refs for use in actions
  const refetchFunctionsRef = useRef({
    parentList: parentListQuery.refetch,
    parentsSummary: parentsSummaryQuery.refetch,
    visitorList: visitorListQuery.refetch,
    visitorsSummary: visitorsSummaryQuery.refetch,
    guestList: guestListQuery.refetch,
    guestsSummary: guestsSummaryQuery.refetch,
  });

  // Update refs when refetch functions change
  useEffect(() => {
    refetchFunctionsRef.current = {
      parentList: parentListQuery.refetch,
      parentsSummary: parentsSummaryQuery.refetch,
      visitorList: visitorListQuery.refetch,
      visitorsSummary: visitorsSummaryQuery.refetch,
      guestList: guestListQuery.refetch,
      guestsSummary: guestsSummaryQuery.refetch,
    };
  }, [
    parentListQuery.refetch,
    parentsSummaryQuery.refetch,
    visitorListQuery.refetch,
    visitorsSummaryQuery.refetch,
    guestListQuery.refetch,
    guestsSummaryQuery.refetch,
  ]);

  // Sync summary data from hooks to state
  useEffect(() => {
    if (parentsSummaryQuery.data) {
      dispatch({
        type: UsersActionType.UPDATE_PARENTS_SUMMARY,
        payload: parentsSummaryQuery.data,
      });
    }
  }, [parentsSummaryQuery.data]);

  useEffect(() => {
    if (visitorsSummaryQuery.data) {
      dispatch({
        type: UsersActionType.UPDATE_VISITORS_SUMMARY,
        payload: visitorsSummaryQuery.data,
      });
    }
  }, [visitorsSummaryQuery.data]);

  useEffect(() => {
    if (guestsSummaryQuery.data) {
      dispatch({
        type: UsersActionType.UPDATE_GUESTS_SUMMARY,
        payload: guestsSummaryQuery.data,
      });
    }
  }, [guestsSummaryQuery.data]);

  // Sync list data from hooks to state
  useEffect(() => {
    if (parentListQuery.data?.data) {
      dispatch({
        type: UsersActionType.SET_PARENT_USERS,
        payload: parentListQuery.data.data,
      });
    }
  }, [parentListQuery.data]);

  useEffect(() => {
    if (visitorListQuery.data?.data) {
      dispatch({
        type: UsersActionType.SET_VISITOR_USERS,
        payload: visitorListQuery.data.data,
      });
    }
  }, [visitorListQuery.data]);

  useEffect(() => {
    if (guestListQuery.data?.data) {
      dispatch({
        type: UsersActionType.SET_GUEST_USERS,
        payload: guestListQuery.data.data,
      });
    }
  }, [guestListQuery.data]);

  useEffect(() => {
    if (internalListQuery.data?.data) {
      dispatch({
        type: UsersActionType.SET_INTERNAL_USERS,
        payload: internalListQuery.data.data,
      });
    }
  }, [internalListQuery.data]);

  // Sync loading states from hooks
  useEffect(() => {
    const isLoading =
      parentListQuery.isLoading ||
      visitorListQuery.isLoading ||
      guestListQuery.isLoading ||
      internalListQuery.isLoading ||
      parentsSummaryQuery.isLoading ||
      visitorsSummaryQuery.isLoading ||
      guestsSummaryQuery.isLoading;
    dispatch({ type: UsersActionType.SET_LOADING, payload: isLoading });
  }, [
    parentListQuery.isLoading,
    visitorListQuery.isLoading,
    guestListQuery.isLoading,
    internalListQuery.isLoading,
    parentsSummaryQuery.isLoading,
    visitorsSummaryQuery.isLoading,
    guestsSummaryQuery.isLoading,
  ]);

  // Sync error states from hooks
  useEffect(() => {
    const error =
      parentListQuery.error?.response?.data?.message ||
      visitorListQuery.error?.response?.data?.message ||
      guestListQuery.error?.response?.data?.message ||
      parentsSummaryQuery.error?.response?.data?.message ||
      visitorsSummaryQuery.error?.response?.data?.message ||
      guestsSummaryQuery.error?.response?.data?.message ||
      null;
    dispatch({ type: UsersActionType.SET_ERROR, payload: error });
  }, [
    parentListQuery.error,
    visitorListQuery.error,
    guestListQuery.error,
    parentsSummaryQuery.error,
    visitorsSummaryQuery.error,
    guestsSummaryQuery.error,
  ]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading:
        parentListQuery.isLoading ||
        visitorListQuery.isLoading ||
        guestListQuery.isLoading ||
        parentsSummaryQuery.isLoading ||
        visitorsSummaryQuery.isLoading ||
        guestsSummaryQuery.isLoading,
    }),
    [
      state,
      parentListQuery.isLoading,
      visitorListQuery.isLoading,
      guestListQuery.isLoading,
      parentsSummaryQuery.isLoading,
      visitorsSummaryQuery.isLoading,
      guestsSummaryQuery.isLoading,
    ]
  );

  return (
    <UsersContext.Provider value={value}>
      <UsersDispatchContext.Provider value={dispatch}>
        <UsersRefetchContext.Provider value={refetchFunctionsRef.current}>
          {children}
        </UsersRefetchContext.Provider>
      </UsersDispatchContext.Provider>
    </UsersContext.Provider>
  );
};

// Custom hooks to use the context
export const useUsersState = (): UsersState => {
  const context = useContext(UsersContext);
  if (context === undefined) {
    throw new Error('useUsersState must be used within a UsersProvider');
  }
  return context;
};

export const useUsersActions = () => {
  const dispatch = useContext(UsersDispatchContext);
  const refetchFunctions = useContext(UsersRefetchContext);

  if (dispatch === undefined) {
    throw new Error('useUsersActions must be used within a UsersProvider');
  }

  if (refetchFunctions === undefined) {
    throw new Error('useUsersActions must be used within a UsersProvider');
  }

  const setUserType = (type: UserType) => {
    dispatch({ type: UsersActionType.SET_USER_TYPE, payload: type });
  };

  const setActiveTab = (index: number) => {
    dispatch({ type: UsersActionType.SET_ACTIVE_TAB, payload: index });
  };

  const setCategory = (category: UserCategory) => {
    dispatch({ type: UsersActionType.SET_CATEGORY, payload: category });
  };

  const setFilters = (filters: Partial<UsersFilters>) => {
    dispatch({ type: UsersActionType.SET_FILTERS, payload: filters });
  };

  const setSearchQuery = (query: string) => {
    dispatch({ type: UsersActionType.SET_SEARCH_QUERY, payload: query });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: UsersActionType.SET_LOADING, payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: UsersActionType.SET_ERROR, payload: error });
  };

  const setSelectedUser = (user: BaseUser | null) => {
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: user });
  };

  const resetFilters = () => {
    dispatch({ type: UsersActionType.RESET_FILTERS });
  };

  // Data fetching methods - use refetch from hooks
  const fetchParentUsers = async (): Promise<void> => {
    try {
      await refetchFunctions.parentList();
      await refetchFunctions.parentsSummary();
    } catch (error) {
      setError('Failed to fetch parent users');
    }
  };

  const fetchVisitorUsers = async (): Promise<void> => {
    try {
      await refetchFunctions.visitorList();
      await refetchFunctions.visitorsSummary();
    } catch (error) {
      setError('Failed to fetch visitor users');
    }
  };

  const fetchGuestUsers = async (): Promise<void> => {
    try {
      await refetchFunctions.guestList();
      await refetchFunctions.guestsSummary();
    } catch (error) {
      setError('Failed to fetch guest users');
    }
  };

  // Utility methods - these need state, so they should be used with useUsersState
  const getFilteredUsers = (state: UsersState): BaseUser[] => {
    let users: BaseUser[] = [];

    switch (state.currentUserType) {
      case UserType.PARENTS:
        users = state.parentUsers;
        break;
      case UserType.VISITORS:
        users = state.visitorUsers;
        break;
      case UserType.GUESTS:
        users = state.guestUsers;
        break;
      case UserType.INTERNAL_USERS:
        users = state.internalUsers;
        break;
    }

    return users.filter((user) => {
      // Filter by status
      if (
        state.filters.status !== 'all' &&
        user.status !== state.filters.status
      ) {
        return false;
      }

      // Filter by search query
      if (state.filters.searchQuery) {
        const searchLower = state.filters.searchQuery.toLowerCase();
        return (
          user.firstName.toLowerCase().includes(searchLower) ||
          user.lastName.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.phone.includes(state.filters.searchQuery)
        );
      }

      return true;
    });
  };

  const getFilteredParentUsers = (state: UsersState): ParentUser[] => {
    return state.parentUsers.filter((user) => {
      // Filter by status
      if (
        state.filters.status !== 'all' &&
        user.status !== state.filters.status
      ) {
        return false;
      }

      // Filter by search query
      if (state.filters.searchQuery) {
        const searchLower = state.filters.searchQuery.toLowerCase();
        return (
          user.firstName.toLowerCase().includes(searchLower) ||
          user.lastName.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.phone.includes(state.filters.searchQuery)
        );
      }

      return true;
    });
  };

  const getFilteredVisitorUsers = (state: UsersState): VisitorUser[] => {
    return state.visitorUsers.filter((user) => {
      // Filter by status
      if (
        state.filters.status !== 'all' &&
        user.status !== state.filters.status
      ) {
        return false;
      }

      // Filter by search query
      if (state.filters.searchQuery) {
        const searchLower = state.filters.searchQuery.toLowerCase();
        return (
          user.firstName.toLowerCase().includes(searchLower) ||
          user.lastName.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.phone.includes(state.filters.searchQuery)
        );
      }

      return true;
    });
  };

  const getFilteredGuestUsers = (state: UsersState): GuestUser[] => {
    return state.guestUsers.filter((user) => {
      // Filter by status
      if (
        state.filters.status !== 'all' &&
        user.status !== state.filters.status
      ) {
        return false;
      }

      // Filter by search query
      if (state.filters.searchQuery) {
        const searchLower = state.filters.searchQuery.toLowerCase();
        return (
          user.firstName.toLowerCase().includes(searchLower) ||
          user.lastName.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.phone.includes(state.filters.searchQuery)
        );
      }

      return true;
    });
  };

  const getCurrentSummary = (state: UsersState): UsersSummary | VisitorsSummary => {
    switch (state.currentUserType) {
      case UserType.PARENTS:
        return state.parentsSummary;
      case UserType.VISITORS:
        return state.visitorsSummary;
      case UserType.GUESTS:
        return state.guestsSummary;
      default:
        return state.parentsSummary;
    }
  };

  const exportUsers = async (
    currentUserType: UserType,
    format: 'pdf' | 'excel'
  ): Promise<void> => {
    try {
      setLoading(true);
      // Simulate export process
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log(`Exporting ${currentUserType} users as ${format}`);
      // TODO: Implement actual export logic
    } catch (error) {
      setError(`Failed to export users as ${format}`);
    } finally {
      setLoading(false);
    }
  };

  // Add/Edit methods for Parents
  const openAddParent = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_PARENT_OPEN,
      payload: true,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: true });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  const openEditParent = (user: ParentUser): void => {
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: user });
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_PARENT_OPEN,
      payload: true,
    });
    dispatch({ type: UsersActionType.SET_IS_EDIT_MODE, payload: true });
  };

  const closeAddEditParent = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_PARENT_OPEN,
      payload: false,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: false });
    dispatch({ type: UsersActionType.SET_IS_EDIT_MODE, payload: false });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  // Add/Edit methods for Visitors
  const openAddVisitor = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_VISITOR_OPEN,
      payload: true,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: true });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  const openEditVisitor = (user: VisitorUser): void => {
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: user });
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_VISITOR_OPEN,
      payload: true,
    });
    dispatch({ type: UsersActionType.SET_IS_EDIT_MODE, payload: true });
  };

  const closeAddEditVisitor = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_VISITOR_OPEN,
      payload: false,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: false });
    dispatch({ type: UsersActionType.SET_IS_EDIT_MODE, payload: false });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  // Add/Edit methods for Guests
  const openAddGuest = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_GUEST_OPEN,
      payload: true,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: true });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  const openEditGuest = (user: GuestUser): void => {
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: user });
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_GUEST_OPEN,
      payload: true,
    });
    dispatch({ type: UsersActionType.SET_IS_EDIT_MODE, payload: true });
  };

  const closeAddEditGuest = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_GUEST_OPEN,
      payload: false,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: false });
    dispatch({ type: UsersActionType.SET_IS_EDIT_MODE, payload: false });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  const openAddInternal = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_INTERNAL_OPEN,
      payload: true,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: true });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  const closeAddEditInternal = (): void => {
    dispatch({
      type: UsersActionType.SET_IS_ADD_EDIT_INTERNAL_OPEN,
      payload: false,
    });
    dispatch({ type: UsersActionType.SET_IS_CREATE_MODE, payload: false });
    dispatch({ type: UsersActionType.SET_SELECTED_USER, payload: null });
  };

  return {
    setUserType,
    setActiveTab,
    setCategory,
    setFilters,
    setSearchQuery,
    setLoading,
    setError,
    setSelectedUser,
    resetFilters,
    fetchParentUsers,
    fetchVisitorUsers,
    fetchGuestUsers,
    getFilteredUsers,
    getFilteredParentUsers,
    getFilteredVisitorUsers,
    getFilteredGuestUsers,
    getCurrentSummary,
    exportUsers,
    openAddParent,
    openEditParent,
    closeAddEditParent,
    openAddVisitor,
    openEditVisitor,
    closeAddEditVisitor,
    openAddGuest,
    openEditGuest,
    closeAddEditGuest,
    openAddInternal,
    closeAddEditInternal,
  };
};

// Legacy hook for backward compatibility
export const useUsers = () => {
  const state = useUsersState();
  const actions = useUsersActions();

  // Wrap utility methods to automatically use current state
  return {
    state,
    ...actions,
    getFilteredUsers: () => actions.getFilteredUsers(state),
    getFilteredParentUsers: () => actions.getFilteredParentUsers(state),
    getFilteredVisitorUsers: () => actions.getFilteredVisitorUsers(state),
    getFilteredGuestUsers: () => actions.getFilteredGuestUsers(state),
    getCurrentSummary: () => actions.getCurrentSummary(state),
    exportUsers: (format: 'pdf' | 'excel') =>
      actions.exportUsers(state.currentUserType, format),
  };
};

export default UsersContext;
